import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { SkeletonCard } from '@/components/ui/SkeletonLoader';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { planoAdminService } from '@/services/api';
import { Plano, PlanoCreateInput, PlanoUpdateInput } from '@/types';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import toast from 'react-hot-toast';

const TIPOS = ['Gratuito', 'Pago'] as const;
const PERIODICIDADES = ['', 'Mensal', 'Semestral', 'Anual'] as const;
const STATUS = ['Ativo', 'Inativo'] as const;

function emptyForm(): PlanoCreateInput {
  return {
    nome: '',
    slug: '',
    descricao: '',
    tipo: 'Gratuito',
    periodicidade: null,
    diasTeste: 14,
    limiteGeracaoTermoMensal: null,
    limiteRescisaoMensal: null,
    limiteRelatorioMensal: null,
    podeGerenciarUsuarios: false,
    podeExportarRelatorios: false,
    podeAprovarEstagios: false,
    podeGerarTermo: true,
    podeGerarRescisao: true,
    valorMensal: null,
    status: 'Ativo',
  };
}

function planoToForm(p: Plano): PlanoCreateInput {
  return {
    nome: p.nome,
    slug: p.slug,
    descricao: p.descricao ?? '',
    tipo: p.tipo as PlanoCreateInput['tipo'],
    periodicidade: p.periodicidade,
    diasTeste: p.diasTeste,
    limiteGeracaoTermoMensal: p.limiteGeracaoTermoMensal,
    limiteRescisaoMensal: p.limiteRescisaoMensal,
    limiteRelatorioMensal: p.limiteRelatorioMensal,
    podeGerenciarUsuarios: p.podeGerenciarUsuarios,
    podeExportarRelatorios: p.podeExportarRelatorios,
    podeAprovarEstagios: p.podeAprovarEstagios,
    podeGerarTermo: p.podeGerarTermo,
    podeGerarRescisao: p.podeGerarRescisao,
    valorMensal: p.valorMensal,
    status: p.status,
  };
}

function parseOptionalInt(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function parseOptionalNumber(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  const n = parseFloat(t.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export const AdminPlanosPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { handleError, handleSuccess } = useErrorHandler();
  const { dialogState, showConfirm, closeDialog } = useConfirmDialog();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<PlanoCreateInput>(emptyForm);

  const [loadError, setLoadError] = useState<string | null>(null);

  const { data: planos = [], isLoading, error } = useQuery({
    queryKey: ['admin-planos'],
    queryFn: () => planoAdminService.list(),
  });

  useEffect(() => {
    if (error) {
      const message = handleError(error as any, 'Erro ao carregar planos');
      setLoadError(message);
    }
  }, [error, handleError]);

  useEffect(() => {
    if (planos) setLoadError(null);
  }, [planos]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (p: Plano) => {
    setEditingId(p.id);
    setForm(planoToForm(p));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editingId != null) {
        const payload: PlanoUpdateInput = {
          nome: form.nome,
          slug: form.slug,
          descricao: form.descricao || null,
          tipo: form.tipo,
          periodicidade: form.periodicidade,
          diasTeste: form.diasTeste,
          limiteGeracaoTermoMensal: form.limiteGeracaoTermoMensal,
          limiteRescisaoMensal: form.limiteRescisaoMensal,
          limiteRelatorioMensal: form.limiteRelatorioMensal,
          podeGerenciarUsuarios: form.podeGerenciarUsuarios,
          podeExportarRelatorios: form.podeExportarRelatorios,
          podeAprovarEstagios: form.podeAprovarEstagios,
          podeGerarTermo: form.podeGerarTermo,
          podeGerarRescisao: form.podeGerarRescisao,
          valorMensal: form.valorMensal,
          status: form.status,
        };
        return planoAdminService.update(editingId, payload);
      }
      return planoAdminService.create(form);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-planos'] });
      handleSuccess(editingId != null ? 'Plano atualizado.' : 'Plano criado.');
      closeModal();
    },
    onError: (err: any) => {
      handleError(err, 'Não foi possível salvar o plano');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: planoAdminService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-planos'] });
      handleSuccess('Plano excluído.');
    },
    onError: (err: any) => {
      handleError(err, 'Não foi possível excluir o plano');
    },
  });

  const handleDelete = (p: Plano) => {
    showConfirm(
      'Excluir plano',
      `Excluir o plano "${p.nome}"? Não será possível se houver usuários vinculados.`,
      async () => {
        await deleteMutation.mutateAsync(p.id);
      },
      'danger'
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.slug.trim()) {
      toast.error('Nome e slug são obrigatórios.');
      return;
    }
    saveMutation.mutate();
  };

  if (loadError) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Planos</h1>
          <p className="mt-1 text-sm text-gray-500">CRUD de planos (somente administradores)</p>
        </div>
        <Button type="button" onClick={openCreate}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Novo plano
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de planos</CardTitle>
          <CardDescription>{planos.length} plano(s)</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonCard count={4} />
          ) : planos.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Nenhum plano cadastrado.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Nome</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Slug</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Tipo</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Periodicidade</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-700">Valor</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-700">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {planos.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900">{p.nome}</td>
                      <td className="px-3 py-2 text-gray-600">{p.slug}</td>
                      <td className="px-3 py-2">{p.tipo}</td>
                      <td className="px-3 py-2">{p.periodicidade ?? '—'}</td>
                      <td className="px-3 py-2 text-right">
                        {p.valorMensal != null ? `R$ ${Number(p.valorMensal).toFixed(2)}` : '—'}
                      </td>
                      <td className="px-3 py-2">{p.status}</td>
                      <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                        <Button type="button" variant="outline" size="sm" onClick={() => openEdit(p)} title="Editar">
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(p)}
                          disabled={deleteMutation.isPending}
                          className="text-red-600 hover:text-red-700"
                          title="Excluir"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Transition appear show={modalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => !saveMutation.isPending && closeModal()}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-6 shadow-xl">
                  <div className="flex justify-between items-start mb-4">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      {editingId != null ? 'Editar plano' : 'Novo plano'}
                    </Dialog.Title>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-600"
                      onClick={closeModal}
                      disabled={saveMutation.isPending}
                      aria-label="Fechar"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                    <div>
                      <Label htmlFor="plano-nome">Nome</Label>
                      <Input
                        id="plano-nome"
                        value={form.nome}
                        onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="plano-slug">Slug</Label>
                      <Input
                        id="plano-slug"
                        value={form.slug}
                        onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="plano-desc">Descrição</Label>
                      <textarea
                        id="plano-desc"
                        rows={2}
                        className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                        value={form.descricao ?? ''}
                        onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="plano-tipo">Tipo</Label>
                        <select
                          id="plano-tipo"
                          className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
                          value={form.tipo ?? 'Gratuito'}
                          onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))}
                        >
                          {TIPOS.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="plano-per">Periodicidade</Label>
                        <select
                          id="plano-per"
                          className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
                          value={form.periodicidade ?? ''}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              periodicidade: e.target.value === '' ? null : e.target.value,
                            }))
                          }
                        >
                          {PERIODICIDADES.map((p) => (
                            <option key={p || 'null'} value={p}>
                              {p === '' ? '(nenhuma)' : p}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="plano-dias">Dias de teste</Label>
                        <Input
                          id="plano-dias"
                          type="number"
                          min={0}
                          value={form.diasTeste ?? 0}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            setForm((f) => ({ ...f, diasTeste: Number.isFinite(n) ? n : 0 }));
                          }}
                        />
                      </div>
                      <div>
                        <Label htmlFor="plano-valor">Valor mensal (R$)</Label>
                        <Input
                          id="plano-valor"
                          type="number"
                          step="0.01"
                          min={0}
                          value={form.valorMensal ?? ''}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              valorMensal: e.target.value === '' ? null : parseOptionalNumber(e.target.value),
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label htmlFor="lim-t">Lim. termo/mês</Label>
                        <Input
                          id="lim-t"
                          type="number"
                          min={0}
                          value={form.limiteGeracaoTermoMensal ?? ''}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, limiteGeracaoTermoMensal: parseOptionalInt(e.target.value) }))
                          }
                        />
                      </div>
                      <div>
                        <Label htmlFor="lim-r">Lim. rescisão/mês</Label>
                        <Input
                          id="lim-r"
                          type="number"
                          min={0}
                          value={form.limiteRescisaoMensal ?? ''}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, limiteRescisaoMensal: parseOptionalInt(e.target.value) }))
                          }
                        />
                      </div>
                      <div>
                        <Label htmlFor="lim-rp">Lim. relatório/mês</Label>
                        <Input
                          id="lim-rp"
                          type="number"
                          min={0}
                          value={form.limiteRelatorioMensal ?? ''}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, limiteRelatorioMensal: parseOptionalInt(e.target.value) }))
                          }
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!form.podeGerenciarUsuarios}
                          onChange={(e) => setForm((f) => ({ ...f, podeGerenciarUsuarios: e.target.checked }))}
                        />
                        Gerenciar usuários
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!form.podeExportarRelatorios}
                          onChange={(e) => setForm((f) => ({ ...f, podeExportarRelatorios: e.target.checked }))}
                        />
                        Exportar relatórios
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!form.podeAprovarEstagios}
                          onChange={(e) => setForm((f) => ({ ...f, podeAprovarEstagios: e.target.checked }))}
                        />
                        Aprovar estágios
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!form.podeGerarTermo}
                          onChange={(e) => setForm((f) => ({ ...f, podeGerarTermo: e.target.checked }))}
                        />
                        Gerar termo
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!form.podeGerarRescisao}
                          onChange={(e) => setForm((f) => ({ ...f, podeGerarRescisao: e.target.checked }))}
                        />
                        Gerar rescisão
                      </label>
                    </div>
                    <div>
                      <Label htmlFor="plano-st">Status</Label>
                      <select
                        id="plano-st"
                        className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
                        value={form.status ?? 'Ativo'}
                        onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                      >
                        {STATUS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={closeModal} disabled={saveMutation.isPending}>
                        Cancelar
                      </Button>
                      <Button type="submit" disabled={saveMutation.isPending}>
                        {saveMutation.isPending ? 'Salvando…' : 'Salvar'}
                      </Button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText="Excluir"
        cancelText="Cancelar"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};
